/**
 * Shared visual-viewport sizing for open overlays that need a single scroll
 * owner (hamburger panel, QuickSearch suggestions, similar shells).
 * Prefer visualViewport so iOS chrome / keyboard-adjacent chrome is reflected.
 */
export function getVisualViewportHeight(
  win = typeof window !== 'undefined' ? window : undefined
) {
  if (!win) {
    return 0;
  }

  const visualHeight = win.visualViewport && win.visualViewport.height;
  if (typeof visualHeight === 'number' && visualHeight > 0) {
    return visualHeight;
  }

  return typeof win.innerHeight === 'number' ? win.innerHeight : 0;
}

/**
 * How much of the visual viewport is already used above the scroll surface.
 * `anchorEdgePx` is the Y distance from the layout viewport top to the top edge
 * of that surface (e.g. sticky chrome `getBoundingClientRect().bottom`, or a
 * suggestions list `getBoundingClientRect().top`). Subtract
 * visualViewport.offsetTop so iOS URL-bar scroll does not leave a gap.
 */
export function getOccupiedViewportPx(
  anchorEdgePx,
  visualViewportOffsetTop = 0
) {
  const edge = Number(anchorEdgePx);
  const offset = Number(visualViewportOffsetTop);
  const safeEdge = Number.isFinite(edge) ? edge : 0;
  const safeOffset = Number.isFinite(offset) ? offset : 0;
  return Math.max(0, safeEdge - safeOffset);
}

/**
 * Remaining viewport under occupied chrome for a scrollable overlay.
 * @param {number} occupiedPx Space already used above the surface
 * @param {number} [viewportHeight] Defaults to getVisualViewportHeight()
 * @returns {number} Non-negative height in CSS pixels
 */
export function getRemainingViewportHeight(
  occupiedPx,
  viewportHeight = getVisualViewportHeight()
) {
  const occupied = Number(occupiedPx);
  const viewport = Number(viewportHeight);
  const safeOccupied = Number.isFinite(occupied) ? occupied : 0;
  const safeViewport = Number.isFinite(viewport) ? viewport : 0;
  return Math.max(0, safeViewport - safeOccupied);
}
